import React from 'react';
import { Link } from 'react-router-dom';
import { Stethoscope, BookOpen, Calculator, ArrowRight } from 'lucide-react';
import { RELATED_LINKS } from '../data/internalLinks';

const groupMeta = {
  programs: { label: 'Related Programs', icon: Stethoscope, color: 'text-emerald-400' },
  guides: { label: 'Related Guides', icon: BookOpen, color: 'text-blue-400' },
  tools: { label: 'Related Calculators', icon: Calculator, color: 'text-orange-400' },
};

const LinkGroup = ({ groupKey, items }) => {
  if (!items || items.length === 0) return null;
  const meta = groupMeta[groupKey];
  const Icon = meta.icon;
  return (
    <div data-testid={`related-group-${groupKey}`}>
      <p className={`text-xs font-semibold uppercase tracking-wide mb-3 flex items-center gap-1.5 ${meta.color}`}>
        <Icon className="w-3.5 h-3.5" aria-hidden="true" /> {meta.label}
      </p>
      <ul className="space-y-2">
        {items.map((it, i) => (
          <li key={i}>
            <Link
              to={it.to}
              className="text-slate-300 hover:text-white text-sm inline-flex items-center gap-1 group transition-colors"
              data-testid={`related-link-${it.to.replace(/\//g, '-').replace(/^-/, '')}`}
            >
              {it.label}
              <ArrowRight className="w-3 h-3 opacity-0 group-hover:opacity-100 -translate-x-1 group-hover:translate-x-0 transition-all" aria-hidden="true" />
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
};

/**
 * Site-wide internal-linking block. Renders related programs, guides and
 * calculators for a given page slug. Returns null if the slug has no map entry.
 */
const RelatedResources = ({ slug, title = 'Explore Related Resources', className = '' }) => {
  const data = RELATED_LINKS[slug];
  if (!data) return null;

  const groups = ['programs', 'guides', 'tools'].filter((g) => data[g] && data[g].length > 0);
  if (groups.length === 0) return null;

  return (
    <section
      className={`bg-slate-900/50 border border-slate-800 rounded-2xl p-6 sm:p-8 ${className}`}
      aria-labelledby="related-resources-heading"
      data-testid="related-resources"
    >
      <h2 id="related-resources-heading" className="text-lg sm:text-xl font-bold text-white mb-6">
        {title}
      </h2>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {groups.map((g) => (
          <LinkGroup key={g} groupKey={g} items={data[g]} />
        ))}
      </div>
    </section>
  );
};

export default RelatedResources;
